import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import TableCell from "@mui/material/TableCell";
import TableRowMui from "@mui/material/TableRow";

import { Badge, BadgeColors, colors } from "@dotnet/telematics-design-system";

import DateTime from "components/DateTime";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import { TableItemInstanceProps } from "components/TaskGroups/types/interfaces";
import TableContext from "context/table/tableContext";
import { useOverflow } from "shared/hooks/useOverflow";
import {
    tableColumnDropShadow,
    tableColumnRightDropShadow
} from "shared/styles/styleFunctions";

import ApproveDiscard from "../ApproveDiscard";
import TaskStats from "../TaskStats";

const TableRow = ({
    data,
    isApprovalRequired,
    isApprovalBadgeRendered,
    isSelected,
    scrollHorizontal
}: TableItemInstanceProps) => {
    const {
        id,
        company_name,
        task_type,
        type,
        file,
        objects,
        created_at,
        created_by,
        task_stats
    } = data;

    const { gray50, gray200, gray900, textPlaceholder, white } = colors;

    const { rows, setDetailsData } = useContext(TableContext);

    const { t } = useTranslation();

    const { ref: measuredRefFile, isOverflowing: isOverflowingFile } =
        useOverflow([rows]);

    const { ref: measuredRefCompany, isOverflowing: isOverflowingCompany } =
        useOverflow([rows]);

    const handleClick = () => {
        if (!isSelected) {
            setDetailsData(data);
        }
    };

    return (
        <TableRowMui
            data-testid="table-row-component"
            onClick={handleClick}
            selected={isSelected}
            css={css({
                cursor: "pointer",

                td: {
                    background: white
                },

                "&:hover td": {
                    background: gray50
                }
            })}
        >
            <TableCell
                css={css([
                    {
                        position: "sticky",
                        left: "0",
                        maxWidth: "314px",

                        "& > div": {
                            overflowX: "hidden",
                            textOverflow: "ellipsis"
                        }
                    },
                    tableColumnRightDropShadow(scrollHorizontal)
                ])}
            >
                <div>
                    {task_type
                        ? t(`General##${task_type}`)
                        : t(`Task groups##bulk type##${type}`)}
                </div>

                {file && (
                    <TooltipGeneral
                        disableHoverListener={!isOverflowingFile}
                        placement={TOOLTIP_PLACEMENT.Top}
                        title={file.name}
                    >
                        <div ref={measuredRefFile}>({file.name})</div>
                    </TooltipGeneral>
                )}
            </TableCell>

            <TableCell
                css={css({
                    span: {
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "20px",
                        letterSpacing: "0.1px",
                        color: gray900
                    },

                    "span:first-of-type": {
                        color: textPlaceholder,
                        marginRight: "5px"
                    }
                })}
            >
                <div>
                    <span>{t("General##id")}:</span>
                    <span>{id}</span>
                </div>
            </TableCell>

            <TableCell
                css={css({
                    maxWidth: "300px",

                    "& > div": {
                        overflowX: "hidden",
                        textOverflow: "ellipsis"
                    },

                    span: {
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "20px",
                        letterSpacing: "0.1px",
                        color: gray900
                    },

                    "span:first-of-type": {
                        color: textPlaceholder,
                        marginRight: "5px"
                    }
                })}
            >
                <TooltipGeneral
                    disableHoverListener={!isOverflowingCompany}
                    placement={TOOLTIP_PLACEMENT.Top}
                    title={company_name}
                >
                    <div ref={measuredRefCompany}>
                        <span>{t("General##company")}:</span>
                        <span>{company_name}</span>
                    </div>
                </TooltipGeneral>
            </TableCell>

            <TableCell
                css={css({
                    maxWidth: "326px",

                    "& > div": {
                        overflowX: "hidden",
                        textOverflow: "ellipsis"
                    },

                    "& > div:first-of-type": {
                        marginBottom: "8px"
                    },

                    "span:first-of-type": {
                        color: textPlaceholder,
                        marginRight: "5px"
                    }
                })}
            >
                <div>
                    <span>{t("General##created at")}:</span>

                    <span>
                        <DateTime date={created_at} />
                    </span>
                </div>

                {created_by && (
                    <div>
                        <span>{t("General##created by")}:</span>
                        <span>{created_by.email}</span>
                    </div>
                )}
            </TableCell>

            <TableCell>
                {isApprovalBadgeRendered ? (
                    <Badge color={BadgeColors.light}>
                        {objects} {t("General##Pending approval")}
                    </Badge>
                ) : (
                    <TaskStats data={task_stats} />
                )}
            </TableCell>

            {isApprovalRequired ? (
                <TableCell
                    onClick={e => e.stopPropagation()}
                    css={css([
                        {
                            position: "sticky",
                            right: "0",
                            borderLeft: `1px solid ${gray200}`,
                            width: "0",
                            cursor: "auto",

                            "& > span": {
                                marginLeft: "12px"
                            }
                        },
                        tableColumnDropShadow(scrollHorizontal)
                    ])}
                >
                    <ApproveDiscard id={id} />
                </TableCell>
            ) : (
                <TableCell />
            )}
        </TableRowMui>
    );
};

export default TableRow;
